import React from 'react';
import PropTypes from 'prop-types';
import DateRangePicker from './DateRangePicker';
import CustomDateRangePicker from './CustomDateRangePicker';
import PlatformSelector from './PlatformSelector';
import MetricCompareToggle from './MetricCompareToggle';

/**
 * Filter Bar Component
 * Flexible filter bar that conditionally renders filter controls based on provided props
 * @param {Object} props - Component props
 * @param {string|Object} [props.period] - Selected time period (string or {start, end})
 * @param {Function} [props.onPeriodChange] - Period change handler
 * @param {boolean} [props.useCustomDateRange] - Use custom date range picker instead of preset periods
 * @param {string} [props.platform] - Selected platform
 * @param {Function} [props.onPlatformChange] - Platform change handler
 * @param {string} [props.comparisonMode] - Comparison mode
 * @param {Function} [props.onComparisonModeChange] - Comparison mode change handler
 * @param {string} [props.status] - Selected status
 * @param {Function} [props.onStatusChange] - Status change handler
 * @param {string} [props.search] - Search query
 * @param {Function} [props.onSearchChange] - Search change handler
 * @param {React.ReactNode} [props.rightActions] - Actions to display on the right side
 * @param {string} [props.className] - Additional CSS classes
 */
const FilterBar = ({
  period,
  onPeriodChange,
  platform,
  onPlatformChange,
  comparisonMode,
  onComparisonModeChange,
  status,
  onStatusChange,
  search,
  onSearchChange,
  rightActions,
  className = '',
  useCustomDateRange = true
}) => {
  return (
    <div className={`filter-bar ${className}`}>
      <div className="filter-bar-left">
        {typeof onPeriodChange === 'function' && (
          useCustomDateRange ? (
            <CustomDateRangePicker period={period} onPeriodChange={onPeriodChange} className="filter-item" />
          ) : (
            <DateRangePicker period={period} onPeriodChange={onPeriodChange} className="filter-item" />
          )
        )}
        {typeof onPlatformChange === 'function' && (
          <PlatformSelector platform={platform} onPlatformChange={onPlatformChange} className="filter-item" />
        )}
        {typeof onComparisonModeChange === 'function' && (
          <MetricCompareToggle comparisonMode={comparisonMode} onComparisonModeChange={onComparisonModeChange} className="filter-item" />
        )}
        {typeof onStatusChange === 'function' && (
          <div className="status-selector filter-item">
            <label className="filter-label">Status</label>
            <select
              value={status || ''}
              onChange={(e) => onStatusChange(e.target.value || null)}
              className="status-select"
            >
              <option value="">All</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        )}
        {typeof onSearchChange === 'function' && (
          <div className="search-input filter-item">
            <input
              type="search"
              value={search || ''}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search..."
              aria-label="Search"
            />
          </div>
        )}
      </div>
      <div className="filter-bar-right">
        {rightActions}
      </div>
    </div>
  );
};

FilterBar.propTypes = {
  period: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    })
  ]),
  onPeriodChange: PropTypes.func,
  platform: PropTypes.string,
  onPlatformChange: PropTypes.func,
  comparisonMode: PropTypes.string,
  onComparisonModeChange: PropTypes.func,
  status: PropTypes.string,
  onStatusChange: PropTypes.func,
  search: PropTypes.string,
  onSearchChange: PropTypes.func,
  rightActions: PropTypes.node,
  className: PropTypes.string,
  useCustomDateRange: PropTypes.bool
};

export default FilterBar;


